import React, { type ReactNode, useEffect, useState } from 'react'

import { PhotoTableCellContent } from '@components/common/PhotoTableCellContent'
import { PhotoThumbnail } from '@components/common/PhotoThumbnail'
import {
  Checkbox,
  type Color,
  ColorItem,
  Input,
  useMediaQueryContext,
} from '@fourthwall/components'

import { QuantityDropdown } from '../../../../components/QuantityDropdown/QuantityDropdown'
import { VariantPrice } from '../../../../components/VariantPrice/VariantPrice'
import type { Item, ModalItem } from '../../SelectItems.types'
import { isOfferItemType } from '../../utils/isOfferItemType'
import { isSingleVariantOfferItemType } from '../../utils/isSingleVariantOfferItemType'
import { CheckboxColorLabel } from '../CheckboxColorLabel/CheckboxColorLabel'
import * as S from './SelectItem.styled'

export type SelectItemProps = {
  item: Item
  initialValues: {
    checked: boolean
    quantity: string
  }
  onChange: (product: ModalItem) => void
}

export const SelectItem = ({ item, initialValues, onChange }: SelectItemProps) => {
  const [isDirty, setIsDirty] = useState(false)
  const [checked, setChecked] = useState(initialValues.checked)
  const [quantity, setQuantity] = useState(initialValues.quantity)
  const [quantityError, setQuantityError] = useState('')
  const media = useMediaQueryContext()
  const isSingleVariantType = isSingleVariantOfferItemType(item)
  const isOfferType = isOfferItemType(item)

  const handleItemChange = () =>
    !isOfferItemType(item) &&
    onChange({
      variantId: item.variantId,
      quantity,
      price: item.price,
      error: !!quantityError,
      checked,
    })

  const getQuantityError = (item: Item, quantity: string) => {
    const quantityNumber = Number(quantity)

    if (quantityNumber % 1 !== 0) {
      return `Must be whole number.`
    }

    if (quantityNumber < 1) {
      return `At least 1 is required.`
    }

    if ('stock' in item && !!item.stock && quantityNumber > item.stock) {
      return `Only ${item.stock} in stock.`
    }

    return ''
  }

  useEffect(() => {
    if (isDirty) {
      handleItemChange()
    }
  }, [checked, isDirty])

  useEffect(() => {
    if (checked && isDirty) {
      handleItemChange()
    }

    setQuantityError(getQuantityError(item, quantity))
  }, [quantity, quantityError])

  const handleCheckboxValueChange = (value: boolean) => {
    setIsDirty(true)
    setChecked(value)
  }

  const handleQuantityValueChange = (value: string) => {
    setIsDirty(true)
    setQuantity(value)
  }

  const photo = (isSingleVariantType || isOfferType) && (
    <PhotoTableCellContent name={item.name} photo={item.image} photoSize="small" />
  )

  const stock =
    !isOfferType && (typeof item.stock === 'number' ? `${item.stock} in stock` : 'Unlimited stock')

  const renderCheckboxWithLabel = (label: ReactNode) => (
    <S.CheckboxWrapper>
      <Checkbox
        value={checked}
        label={label}
        disabled={'disabled' in item ? item.disabled : undefined}
        onValueChange={handleCheckboxValueChange}
      />
    </S.CheckboxWrapper>
  )

  const renderName = () => {
    if (isOfferType) {
      return photo
    }

    if (isSingleVariantType) {
      return renderCheckboxWithLabel(photo)
    }

    return (
      <S.Indent>
        {renderCheckboxWithLabel(<CheckboxColorLabel label={item.name} color={item.color} />)}
      </S.Indent>
    )
  }

  const renderQuantity = () => {
    if (isOfferType) {
      return null
    }

    return (
      <Input
        type="number"
        hideSpinner={media.tablet}
        size={media.tablet ? 'small' : 'large'}
        fullWidth
        value={quantity}
        label="Quantity"
        min={1}
        max={item.stock}
        error={quantityError}
        disabled={item.disabled}
        onValueChange={handleQuantityValueChange}
      />
    )
  }

  const renderPrice = () => {
    if (isOfferType || !item.price) {
      return null
    }

    return (
      <VariantPrice
        price={item.price}
        oldPrice={item.oldPrice}
        tooltip="You only pay the manufacturing price for samples"
      />
    )
  }

  const renderProduct = () => (
    <S.Product>
      {isSingleVariantType || isOfferType ? (
        <PhotoThumbnail url={item.image} size="small" />
      ) : (
        item.color && <ColorItem color={item.color as Color} />
      )}
      <S.ProductDetails>
        <div>{item.name}</div>
        {!isOfferType && (
          <S.QuantityButton>
            Quantity:{' '}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div onClick={(e) => e.preventDefault()}>
              <QuantityDropdown
                title={item.name}
                description={stock || undefined}
                quantity={quantity}
                maxValue={item.stock}
                disabled={item.disabled}
                onSubmit={handleQuantityValueChange}
              />
            </div>
          </S.QuantityButton>
        )}
        {stock && <S.Stock>{stock}</S.Stock>}
      </S.ProductDetails>
    </S.Product>
  )

  const renderLeftContent = () => {
    if (isOfferType) {
      return renderProduct()
    }

    if (isSingleVariantType) {
      return renderCheckboxWithLabel(renderProduct())
    }

    return <S.Indent>{renderCheckboxWithLabel(renderProduct())}</S.Indent>
  }

  const renderContent = () => {
    if (!media.tablet) {
      return <S.LeftSide>{renderLeftContent()}</S.LeftSide>
    }

    return (
      <>
        <S.Name>{renderName()}</S.Name>
        <S.Stock>{stock}</S.Stock>
        <S.Quantity>{renderQuantity()}</S.Quantity>
      </>
    )
  }

  return (
    <S.Container>
      {renderContent()}
      <S.Price>{renderPrice()}</S.Price>
    </S.Container>
  )
}
